import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { ChipModule } from 'primeng/chip';
import { NavController } from '@ionic/angular/standalone';
import { OrderApiService } from '../../../../services/order-api.service';
import { OrderService } from '../../../../services/order.service';
import { Order, OrderStatus } from '../../../../interfaces/order';
import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'app-orders-list',
  templateUrl: './orders-list.component.html',
  styleUrls: ['./orders-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    SkeletonModule,
    ChipModule
  ]
})
export class OrdersListComponent implements OnInit {
  orders: Order[] = [];
  isLoading = true;

  constructor(
    private _nav: NavController,
    private _orderApi: OrderApiService,
    private _orderService: OrderService,
    private _toast: ToastService
  ) {}

  ngOnInit() {
    this.loadOrders();
  }

  async loadOrders() {
    this.isLoading = true;
    try {
      const response = await this._orderApi.getMyOrders(1, 5).toPromise();
      if (response) {
        this.orders = response.orders;
        console.log('Orders loaded:', this.orders);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Bestellungen konnten nicht geladen werden'
      });
    } finally {
      this.isLoading = false;
    }
  }

  viewOrder(order: Order) {
    this._nav.navigateForward(['/dashboard/orders', order.id]);
  }

  viewAllOrders() {
    this._nav.navigateForward(['/dashboard/orders']);
  }

  getStatusInfo(status: OrderStatus) {
    return this._orderService.getStatusInfo(status);
  }

  // ✅ NEW: Get delivery method icon
  getDeliveryIcon(deliveryMethod: string): string {
    return deliveryMethod === 'Click and Collect' ? 'pi-shopping-bag' : 'pi-truck';
  }

  // ✅ NEW: Get delivery/pickup time formatted
  getDeliveryTime(order: Order): string | null {
    if (order.deliveryMethod === 'Click and Collect' && order.pickupTime) {
      return this.formatDateTime(order.pickupTime);
    }
    if (order.deliveryMethod === 'Delivery' && order.deliveryTime) {
      return this.formatDateTime(order.deliveryTime);
    }
    return null;
  }

  // ✅ NEW: Format date time (compact format for list)
  formatDateTime(date: Date | string): string {
    const d = new Date(date);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const isTomorrow = d.toDateString() === new Date(now.getTime() + 86400000).toDateString();

    const timeStr = d.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    if (isToday) return `Heute ${timeStr}`;
    if (isTomorrow) return `Morgen ${timeStr}`;
    
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // ✅ NEW: Get total items count
  getTotalItems(order: Order): number {
    return order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  }

  formatOrderDate(date: Date): string {
    const now = new Date();
    const orderDate = new Date(date);
    const diffTime = Math.abs(now.getTime() - orderDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Heute';
    if (diffDays === 1) return 'Gestern';
    if (diffDays < 7) return `vor ${diffDays} Tagen`;
    
    return orderDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit'
    });
  }
}